import { baseApiService } from "./baseApiService";

// Types matching backend DTOs
export interface Rule {
  id: string;
  title: string;
  description?: string;
  severity?: string;
  category?: string;
  expected?: Record<string, unknown>;
  detect?: Record<string, unknown>;
  preferred_clause?: string;
  metadata?: Array<Record<string, unknown>>;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// CompanyRuleDto matching backend response
export interface CompanyRuleDto {
  id: string;
  companyId: string;
  ruleId: string;
  title: string;
  description?: string;
  severity?: string;
  category?: string;
  expected?: Record<string, unknown>;
  detect?: Record<string, unknown>;
  preferred_clause?: string;
  metadata?: Array<Record<string, unknown>>;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateCompanyRuleDto {
  id?: string;
  title: string;
  description?: string;
  severity?: string;
  category?: string;
  expected?: Record<string, unknown>;
  detect?: Record<string, unknown>;
  preferred_clause?: string;
  metadata?: Array<Record<string, unknown>>;
}

export type UpdateCompanyRuleDto = CreateCompanyRuleDto;

export interface CompanyRulesConfigDto {
  rules: CreateCompanyRuleDto[];
}

class ConfigurationService {
  /**
   * Get all rules for a company
   */
  async getByCompanyId(companyId: string): Promise<Rule[]> {
    return baseApiService.get<Rule[]>(`/companies/${companyId}/rules`);
  }

  /**
   * Get company rules using the new API endpoint
   */
  async getCompanyRules(companyId: string): Promise<CompanyRuleDto[]> {
    return baseApiService.get<CompanyRuleDto[]>(
      `/companies/${companyId}/rules`
    );
  }

  /**
   * Get a single rule by ID
   */
  async getById(companyId: string, ruleId: string): Promise<Rule> {
    return baseApiService.get<Rule>(`/companies/${companyId}/rules/${ruleId}`);
  }

  /**
   * Create a new rule
   */
  async create(companyId: string, dto: CreateCompanyRuleDto): Promise<Rule> {
    return baseApiService.post<Rule>(`/companies/${companyId}/rules`, dto);
  }

  /**
   * Update an existing rule
   */
  async update(
    companyId: string,
    ruleId: string,
    dto: UpdateCompanyRuleDto
  ): Promise<void> {
    await baseApiService.put<void>(
      `/companies/${companyId}/rules/${ruleId}`,
      dto
    );
  }

  /**
   * Delete a rule
   */
  async delete(companyId: string, ruleId: string): Promise<void> {
    await baseApiService.delete(`/companies/${companyId}/rules/${ruleId}`);
  }

  /**
   * Bulk update rules (replace all rules for a company)
   */
  async bulkUpdate(
    companyId: string,
    dto: CompanyRulesConfigDto
  ): Promise<void> {
    await baseApiService.post<void>(`/companies/${companyId}/rules/bulk`, dto);
  }

  /**
   * Validate JSON rules structure
   */
  validateRules(rules: CreateCompanyRuleDto[]): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!Array.isArray(rules)) {
      errors.push("Rules must be an array");
      return { valid: false, errors };
    }

    rules.forEach((rule, index) => {
      if (!rule.id) {
        errors.push(`Rule ${index + 1}: Missing required field 'id'`);
      }
      if (!rule.title) {
        errors.push(`Rule ${index + 1}: Missing required field 'title'`);
      }
    });

    return { valid: errors.length === 0, errors };
  }
}

export const configurationService = new ConfigurationService();
